import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { TaskTemplate, TaskType } from '@ndvr/customer-api';

import { CustomerApi } from 'API';
import { useAuthContext } from 'views/auth/Auth';

export const useListTemplatesByAdvisor = (advisorPersonOrgGUID: string) =>
    useQuery<TaskTemplate[]>({
        enabled: !!advisorPersonOrgGUID,
        queryKey: ['task-templates', 'advisor', advisorPersonOrgGUID],
        queryFn: async () => {
            const [templates] = await CustomerApi().listTemplatesByAdvisor({ advisorPersonOrgGUID });
            return templates ?? [];
        },
    });

export type TemplateMutationInput = {
    advisorPersonOrgGUID: string;
    defaultAssignee: string;
    title: string;
    description: string;
    type: TaskType;
};

export const useCreateTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: TemplateMutationInput) => {
            const payload = {
                advisorPersonOrgGUID: data.advisorPersonOrgGUID,
                defaultAssignee: data.defaultAssignee,
                type: data.type,
                title: data.title,
                description: data.description,
            } as unknown as TaskTemplate;
            const [taskTemplateGUID] = await CustomerApi().createTemplate(data.advisorPersonOrgGUID, payload);
            return taskTemplateGUID;
        },
        onSettled: (_result, _error, variables) => {
            if (variables?.advisorPersonOrgGUID) {
                queryClient.invalidateQueries({
                    queryKey: ['task-templates', 'advisor', variables.advisorPersonOrgGUID],
                });
            }
        },
    });
};

export const useDeleteTemplate = () => {
    const queryClient = useQueryClient();
    const { user } = useAuthContext();

    return useMutation({
        mutationFn: async (data: { taskTemplateGUID: string; advisorPersonOrgGUID?: string }) => {
            await CustomerApi().deleteTemplate(data.taskTemplateGUID);
        },
        onSettled: (_result, _error, variables) => {
            const advisor = variables?.advisorPersonOrgGUID || user?.personOrgGUID;
            if (advisor) {
                queryClient.invalidateQueries({ queryKey: ['task-templates', 'advisor', advisor] });
            }
        },
    });
};

export const useUpdateTemplate = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (
            data: TemplateMutationInput & {
                taskTemplateGUID: string;
            },
        ) => {
            const payload = {
                taskTemplateGUID: data.taskTemplateGUID,
                advisorPersonOrgGUID: data.advisorPersonOrgGUID,
                defaultAssignee: data.defaultAssignee,
                type: data.type,
                title: data.title,
                description: data.description,
            } as unknown as TaskTemplate;
            await CustomerApi().updateTemplate(data.taskTemplateGUID, payload);
        },
        onSettled: (_result, _error, variables) => {
            if (variables?.advisorPersonOrgGUID) {
                queryClient.invalidateQueries({
                    queryKey: ['task-templates', 'advisor', variables.advisorPersonOrgGUID],
                });
            }
        },
    });
};
